'use client';

import { Clock3 } from 'lucide-react';
import { HubMapNewsItem, HubType } from '@/types/hubs';
import { formatDateStringWithDelimiter } from '@/utils/formatters/dateFormatter';
import IconAdaptive from '@/components/IconAdaptive';
import TruncateText from '@/components/TruncateText';

export interface ProjectStoryUnitProps {
    projectStory: HubMapNewsItem;
}

export default function ProjectStoryUnit({ projectStory }: ProjectStoryUnitProps) {
    const bgClass = (() => {
        switch (projectStory.hub_type) {
            case HubType.spot:
                return 'bg-color-hub-spot-pin';
            case HubType.festa:
                return 'bg-color-hub-festa-pin';
            case HubType.stand:
                return 'bg-color-hub-stand-pin';
            case HubType.studio:
                return 'bg-color-hub-studio-pin';
            case HubType.schoomyers:
                return 'bg-color-hub-schoomyers-pin';
            case HubType.crew:
                return 'bg-color-hub-crew-pin';
            case HubType.stage:
                return 'bg-color-hub-stage-pin';
            case HubType.in:
                return 'bg-color-hub-in-pin';
            default:
                return 'bg-color-co-gray';
        }
    })();

    return (
        <div
            className="w-full flex flex-col rounded-3xl bg-color-white-base h-full cursor-pointer pt-2 px-2"
            onClick={() => {
                if (projectStory.url) {
                    window.open(projectStory.url, '_blank');
                }
            }}
        >
            <div className="relative w-full">
                <div className="aspect-[3/2] w-full rounded-t-3xl z-10 bg-color-white-base">
                    {projectStory.eyecatch_resource_url ? (
                        <img
                            src={projectStory.eyecatch_resource_url}
                            className="w-full h-full rounded-t-3xl object-cover"
                        />
                    ) : (
                        <img
                            src={`/images/hub/map/icon-${projectStory.hub_type}.svg`}
                            className="w-full h-full rounded-t-3xl object-cover"
                        />
                    )}
                </div>
                <div className="absolute top-2 right-4 font-cantarell font-bold text-xs text-color-white-base rounded-3xl bg-color-base px-4 z-10 uppercase">
                    {projectStory.hub_type}
                </div>
            </div>
            <div className={`h-[4px] w-full ${bgClass}`} />
            <div className="flex flex-col p-2 w-full">
                <div className="flex justify-start items-end w-full">
                    <div className="font-notojp font-medium text-xs text-color-co-gray-dark">
                        {projectStory.hub_name}
                    </div>
                </div>
                <div className="font-corp font-bold text-base text-color-base pb-1">{projectStory.title}</div>
                <div className="flex justify-end items-center w-full font-cantarell font-bold text-[10px] text-color-base pt-2">
                    <IconAdaptive
                        Icon={Clock3}
                        strokeWidth={2.0}
                        baseSize={14}
                        breakpoints={{ md: 14 }}
                        className="mr-[2px]"
                    />
                    <TruncateText
                        text={formatDateStringWithDelimiter(projectStory.published_date)}
                        maxLength={40}
                        breakpoints={{ md: 30 }}
                    />
                </div>
            </div>
        </div>
    );
}
